<template>
  <g
    class="Axis"
    fill="none"
    :font-size="fontSize"
    :text-anchor="textAnchor"
    ref="axis"
  >
    <path
      class="Axis-domain"
      v-if="!hideDomainPath"
      :stroke="domainColor"
      :d="domainPath"
    />

    <datalas-axis-tick
      v-for="(tick, index) in ticks"
      :index="index"
      v-bind="tick"
      :key="tick.val"
      :stroke-color="domainColor"
      :strokeDasharray="strokeDasharrayForTick"
      :hideLabels="orient === 'left'"
      @update:axisTickSize="setAxisTickSize"
    />

    <teleport v-if="orient === 'left'" :to="`#${gridKey}-overlay`" s>
      <g class="Gustavo-grid" :transform="`translate(${left}, ${top})`">
        <g class="Gustavo-grid-axis" :transform="`translate(${xPos}, ${yPos})`">
          <g
            class="Axis"
            fill="none"
            :font-size="fontSize"
            :text-anchor="textAnchor"
            ref="axis"
          >
            <rect
              :x="`-${left}`"
              y="0"
              :width="left"
              :height="chartHeight"
              fill="white"
            />
            <datalas-axis-tick
              v-for="(tick, index) in ticks"
              :index="index"
              v-bind="tick"
              :key="tick.val"
              :left="left"
              hideLines
            />
          </g>
        </g>
      </g>
    </teleport>
  </g>
</template>

<script>
// See https://github.com/d3/d3-axis/blob/8e59287/src/axis.js
import { uniq } from 'lodash';
import { useElementSize } from '@vueuse/core';
import { defineComponent, ref, watch, computed, getCurrentInstance } from 'vue';
import Axis from './axis.vue';

const TOP = 'top';
const RIGHT = 'right';
const BOTTOM = 'bottom';
const LEFT = 'left';
const DY_MAP = {
  [TOP]: '0em',
  [LEFT]: '0.32em',
  [BOTTOM]: '0.71em',
  [RIGHT]: '0.32em',
};

const identity = (x) => x;

export default defineComponent({
  name: 'DatalasFlexibleAxis',

  extends: Axis,

  emits: ['update:axisSize', 'update:axisTickSize'],

  props: {
    countCharsToStartTruncate: Number,
    strokeDasharrayForTick: Number,
    gridKey: String,
    left: Number,
    top: Number,
    xPos: Number,
    yPos: Number,
    chartHeight: Number,
    autoTruncate: Boolean,
  },

  setup(props, { emit }) {
    const axis = ref();
    const vm = getCurrentInstance();
    const { $format } = vm.appContext.config.globalProperties;

    const tickSize = ref({});

    const number = (scale) => {
      return (d) => +scale(d);
    };
    const center = (scale) => {
      // Adjust for 0.5px offset
      const rawOffset = Math.max(0, scale.bandwidth() - 1) / 2;
      const offset = scale.round() ? Math.round(rawOffset) : rawOffset;

      return (d) => number(scale)(d) + offset;
    };

    const isVertical = computed(() => {
      return props.orient === LEFT || props.orient === RIGHT;
    });

    const coercedScale = computed(() => {
      return props.scale || identity;
    });
    const k = computed(() => {
      return props.orient === TOP || props.orient === LEFT ? -1 : 1;
    });
    const x = computed(() => {
      return isVertical.value ? 'x' : 'y';
    });
    const transform = (value) => {
      if (isVertical.value)
        return `translate(0, ${value + 0.5 + props.paddingY})`;
      return `translate(${value + 0.5 + props.paddingX}, 0)`;
    };
    const values = computed(() => {
      if (props.tickValues === null) {
        return coercedScale.value.ticks
          ? coercedScale.value.ticks(...props.tickArguments)
          : coercedScale.value.domain();
      }

      return props.tickValues;
    });

    const format = computed(() => {
      if (props.tickFormat === null) {
        if (props.tickFormatString) {
          return $format(props.tickFormatString);
        }
        if (coercedScale.value.tickFormat) {
          return coercedScale.value.tickFormat(...props.tickArguments);
        }
        return identity;
      }
      return props.tickFormat;
    });

    const spacing = computed(() => {
      return Math.max(props.tickSizeInner, 0) + props.tickPadding;
    });
    const range = computed(() => {
      return coercedScale.value.range();
    });
    const range0 = computed(() => {
      return +(range.value[0] || 0) + 0.5 - props.domainOffset;
    });
    const range1 = computed(() => {
      const i = range.value.length - 1;
      return +(range.value[i] || 0) + 0.5 + props.domainOffset;
    });
    const position = computed(() => {
      const method = coercedScale.value.bandwidth ? center : number;
      return method(coercedScale.value.copy());
    });
    const textAnchor = computed(() => {
      if (props.orient === RIGHT) return 'start';
      return props.orient === LEFT ? 'end' : 'middle';
    });
    const tickRadians = computed(() => {
      return props.tickAngle * (Math.PI / 180);
    });
    const padX = computed(() => {
      return props.paddingX * 2;
    });
    const padY = computed(() => {
      return props.paddingY * 2;
    });

    const ticks = computed(() => {
      const data = isVertical.value
        ? values.value
        : uniq(values.value.concat(coercedScale.value.domain()));

      return data
        .map((val) => {
          const res = {
            [x.value]: k.value * spacing.value,
            [`${x.value}2`]: k.value * props.tickSizeInner,
            dy: DY_MAP[props.orient] || '0.32em',

            label: `${props.tickPrefix}${format.value(val)}${props.tickSuffix}`,
            transform: transform(position.value(val)),
            labelAngle: props.labelAngle,
            labelOffset: props.labelOffset,
            tickAngle: props.tickAngle,
            orient: props.orient,
            val,
            xGrid: props.grid && !isVertical.value,
            yGrid: props.grid && isVertical.value,
            width: props.width + padX.value,
            height: props.height + padY.value,
            countCharsToStartTruncate: props.countCharsToStartTruncate,
            autoTruncate: props.autoTruncate,
          };

          return res;
        })
        .filter(
          (_, i, dataTicks) =>
            !props.outerTicksOnly || i === 0 || i === dataTicks.length - 1,
        );
    });
    const domainPath = computed(() => {
      const tickOuter = k.value * props.tickSizeOuter;

      const xOffset = Math.sin(tickRadians.value) * tickOuter;
      const yOffset = Math.cos(tickRadians.value) * tickOuter;

      const startX = isVertical.value ? yOffset : range0.value - xOffset;
      const startY = isVertical.value ? range0.value + xOffset : yOffset;
      const endX = isVertical.value
        ? yOffset
        : range1.value - xOffset + padX.value;
      const endY = isVertical.value
        ? range1.value + xOffset + padY.value
        : yOffset;
      const startRange = range0.value;
      const endRange =
        range1.value + (isVertical.value ? padY.value : padX.value);

      return isVertical.value
        ? `M${startX},${startY}L0.5,${startRange}V${endRange}L${endX},${endY}`
        : `M${startX},${startY}L${startRange},0.5H${endRange}L${endX},${endY}`;
    });

    const { height: axisHeight, width: axisWidth } = useElementSize(axis);

    const setAxisTickSize = (val) => {
      tickSize.value = Object.assign(tickSize.value || {}, {
        [val.id]: {
          width: val.width,
          height: val.height,
        },
      });
    };

    const axisSize = computed(() => {
      return Object.values(tickSize.value).reduce((acc, item) => {
        acc.width = (acc.width ?? 0) + item.width;
        acc.height = (acc.height ?? 0) + item.height;
        return acc;
      }, {});
    });

    const areAxisTicksRendered = computed(() => {
      return Object.keys(tickSize.value).length === ticks.value.length;
    });

    watch([axisHeight, axisWidth], () => {
      const payload = {
        value: { height: axisHeight.value, width: axisWidth.value },
        orient: props.orient,
      };
      emit('update:axisSize', payload);
    });

    watch(areAxisTicksRendered, (val) => {
      emit('update:axisTickSize', { [props.orient]: axisSize });
    });

    return {
      domainPath,
      ticks,
      textAnchor,
      axis,
      setAxisTickSize,
    };
  },
});
</script>
